import React, { useState } from 'react';
import { Filter, ChevronDown, ChevronUp } from 'lucide-react';
import type { SearchFilters } from '../types/search';

interface SearchFiltersProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  facets: any;
  isOpen: boolean;
  onToggle: () => void;
}

export const SearchFiltersPanel: React.FC<SearchFiltersProps> = ({
  filters,
  onFiltersChange,
  facets,
  isOpen,
  onToggle
}) => {
  const [localFilters, setLocalFilters] = useState<SearchFilters>(filters);

  const handleFilterChange = (key: keyof SearchFilters, value: string | undefined) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };


  const clearFilters = () => {
    const clearedFilters: SearchFilters = {};
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };


  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
      >
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-900 dark:text-white">Filters</span>
          {Object.values(filters).some(v => v) && (
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
              Active
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
          {/* Document Type Filter */}
          {facets?.document_type && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Document Type
              </label>
              <select
                value={localFilters.document_type || ''}
                onChange={(e) => { handleFilterChange('document_type', e.target.value || undefined); onFiltersChange({ ...localFilters, document_type: e.target.value || undefined }); }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Types</option>
                {Object.entries(facets.document_type).map(([type, count]) => (
                  <option key={type} value={type}>
                    {`${type} (${count})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Court Filter */}
          {facets?.court && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Court
              </label>
              <select
                value={localFilters.court || ''}
                onChange={(e) => { handleFilterChange('court', e.target.value || undefined); onFiltersChange({ ...localFilters, court: e.target.value || undefined }); }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Courts</option>
                {Object.entries(facets.court).map(([court, count]) => (
                  <option key={court} value={court}>
                    {`${court} (${count})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Publisher Filter */}
          {facets?.publisher && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Publisher
              </label>
              <select
                value={localFilters.publisher || ''}
                onChange={(e) => { handleFilterChange('publisher', e.target.value || undefined); onFiltersChange({ ...localFilters, publisher: e.target.value || undefined }); }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Publishers</option>
                {Object.entries(facets.publisher).map(([publisher, count]) => (
                  <option key={publisher} value={publisher}>
                    {`${publisher} (${count})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              From Date
            </label>
            <input
              type="date"
              value={localFilters.date_from || ''}
              onChange={(e) => { handleFilterChange('date_from', e.target.value || undefined); onFiltersChange({ ...localFilters, date_from: e.target.value || undefined }); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              To Date
            </label>
            <input
              type="date"
              value={localFilters.date_to || ''}
              onChange={(e) => { handleFilterChange('date_to', e.target.value || undefined); onFiltersChange({ ...localFilters, date_to: e.target.value || undefined }); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* File Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              File Type
            </label>
            <select
              value={localFilters.file_type || ''}
              onChange={(e) => { handleFilterChange('file_type', e.target.value || undefined); onFiltersChange({ ...localFilters, file_type: e.target.value || undefined }); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            >
              <option value="">All File Types</option>
              <option value="pdf">PDF</option>
              <option value="docx">DOCX</option>
              <option value="doc">DOC</option>
              <option value="txt">TXT</option>
            </select>
          </div>

          {/* Filter Actions */}
          <div className="flex space-x-3 pt-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
